import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TEMA } from '../theme';
import { useAuth } from '../context/AuthContext';
import { useDemo } from '../demo';

// Vinsales består av moduler med var sin meny. Ordningen här är ordningen i
// väljaren.
export type Modul = 'restaurang' | 'anbud' | 'inkop';

export interface ModulInfo {
  id: Modul;
  namn: string;
  beskrivning: string;
  startsida: string;
}

export const MODULER: ModulInfo[] = [
  { id: 'restaurang', namn: 'Restaurang', beskrivning: 'Kunder, möten, provningar', startsida: '/idag' },
  { id: 'anbud', namn: 'Anbud', beskrivning: 'Kalender, offerter', startsida: '/anbud' },
  { id: 'inkop', namn: 'Inköp', beskrivning: 'Producenter, inköp, plan', startsida: '/inkop' },
];

// Den som saknar uppgift om moduler har de två som fanns före behörigheterna.
const STANDARDMODULER: Modul[] = ['restaurang', 'anbud'];

// Moduler som visas men inte är färdiga. I demon får de ett piller, så att en
// gäst förstår att det inte är mycket att se där än.
const UNDER_UTVECKLING: Modul[] = ['anbud'];

function UtvecklingsPiller() {
  return (
    <span style={{
      fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', letterSpacing: '0.04em', lineHeight: 1,
      padding: '3px 7px', borderRadius: '20px', whiteSpace: 'nowrap', flexShrink: 0,
      border: `1px solid ${TEMA.border}`, color: TEMA.inkFaint,
    }}>Under utveckling</span>
  );
}

const MODUL_NYCKEL = 'vinsales_modul';
// Äldre nyckel från tiden då modulerna hette spår. Läses en gång så att
// ingen hamnar i fel modul efter bytet.
const GAMMAL_NYCKEL = 'vinsales_spar';

// Sidor som hör till alla moduler. Där behålls den modul man senast var i,
// så att menyn inte hoppar tillbaka till Restaurang från Anbud.
const NEUTRALA_SIDOR = ['/admin', '/kund', '/profil', '/handelselogg'];

function modulFrånSökväg(sökväg: string): Modul | null {
  if (sökväg.startsWith('/anbud')) return 'anbud';
  if (sökväg.startsWith('/inkop')) return 'inkop';
  if (NEUTRALA_SIDOR.some(s => sökväg === s || sökväg.startsWith(`${s}/`))) return null;
  return 'restaurang';
}

function läsSparadModul(): Modul {
  try {
    const v = localStorage.getItem(MODUL_NYCKEL) ?? localStorage.getItem(GAMMAL_NYCKEL);
    return MODULER.some(m => m.id === v) ? (v as Modul) : 'restaurang';
  } catch {
    return 'restaurang';
  }
}

// Vilken modul användaren står i: avgörs av adressen, och på gemensamma sidor
// av den modul man senast var i. Används av både sidomenyn och mobilens
// sidhuvud, så att de alltid visar samma modul.
export function useAktivModul(): Modul {
  const location = useLocation();
  const frånUrl = modulFrånSökväg(location.pathname);
  const [sparad, setSparad] = useState<Modul>(läsSparadModul);

  useEffect(() => {
    if (frånUrl && frånUrl !== sparad) {
      setSparad(frånUrl);
      try {
        localStorage.setItem(MODUL_NYCKEL, frånUrl);
      } catch {
        // Lagring kan vara avstängd; modulen gäller då bara denna session.
      }
    }
  }, [frånUrl, sparad]);

  return frånUrl ?? sparad;
}

// Modulerna användaren har tillgång till. Admin sätter dem under Användare.
export function useTillgängligaModuler(): ModulInfo[] {
  const { profil } = useAuth();
  const valda = (profil?.moduler as Modul[] | undefined) ?? STANDARDMODULER;
  return MODULER.filter(m => valda.includes(m.id));
}

interface Props {
  // Antal saker att åtgärda per modul. Visas som en siffra i listan.
  antal?: Partial<Record<Modul, number>>;
  // 'sidomeny': full bredd under logotypen. 'sidhuvud': liten knapp i mobilens sidhuvud.
  variant?: 'sidomeny' | 'sidhuvud';
  onNavigate?: () => void;
}

export default function Modulvaljare({ antal = {}, variant = 'sidomeny', onNavigate }: Props) {
  const navigate = useNavigate();
  const aktiv = useAktivModul();
  const moduler = useTillgängligaModuler();
  const demo = useDemo();
  const iUtveckling = (m: Modul) => demo && UNDER_UTVECKLING.includes(m);
  const [öppen, setÖppen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Stäng vid klick utanför och med Escape.
  useEffect(() => {
    if (!öppen) return;
    const klick = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setÖppen(false);
    };
    const tangent = (e: KeyboardEvent) => { if (e.key === 'Escape') setÖppen(false); };
    document.addEventListener('mousedown', klick);
    document.addEventListener('touchstart', klick);
    document.addEventListener('keydown', tangent);
    return () => {
      document.removeEventListener('mousedown', klick);
      document.removeEventListener('touchstart', klick);
      document.removeEventListener('keydown', tangent);
    };
  }, [öppen]);

  // Med en enda modul finns inget att välja mellan.
  if (moduler.length < 2) return null;

  const aktivInfo = moduler.find(m => m.id === aktiv) ?? moduler[0];
  // Det som väntar i andra moduler syns på knappen, så att man ser det utan att öppna listan.
  const väntarAnnanstans = moduler.some(m => m.id !== aktiv && (antal[m.id] ?? 0) > 0);
  const sidhuvud = variant === 'sidhuvud';

  function välj(m: ModulInfo) {
    setÖppen(false);
    if (m.id !== aktiv) {
      navigate(m.startsida);
      onNavigate?.();
    }
  }

  const siffra = (n: number, markerad: boolean): React.CSSProperties => ({
    fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', fontWeight: 500, lineHeight: 1,
    minWidth: '18px', textAlign: 'center', padding: '4px 8px', borderRadius: '20px', flexShrink: 0,
    background: 'rgba(224,168,62,0.12)', border: '1px solid rgba(224,168,62,0.35)',
    color: TEMA.accent, opacity: markerad ? 1 : 0.9,
  });

  return (
    <div ref={ref} style={{ position: 'relative', marginBottom: sidhuvud ? 0 : '20px' }}>
      {/* I sidomenyn är modulen menyns rubrik: egen yta, större text och en
          etikett ovanför, som fälten i formulären. */}
      {!sidhuvud && (
        <span style={{
          display: 'block', margin: '0 0 6px 2px', fontFamily: "'IBM Plex Mono', monospace",
          fontSize: '11px', letterSpacing: '0.08em', color: TEMA.inkFaint,
        }}>MODUL</span>
      )}
      <button
        onClick={() => setÖppen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={öppen}
        aria-label={`Modul: ${aktivInfo.namn}. Byt modul`}
        style={sidhuvud ? {
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '6px 12px', borderRadius: '16px', cursor: 'pointer',
          border: `1px solid ${öppen ? TEMA.accent : TEMA.border}`,
          background: 'transparent', color: TEMA.ink,
          fontFamily: "'Inter', sans-serif", fontSize: '13px', fontWeight: 500,
        } : {
          display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
          padding: '11px 12px', borderRadius: '4px', cursor: 'pointer', textAlign: 'left',
          border: `1px solid ${öppen ? TEMA.accent : '#3A3B3F'}`,
          background: TEMA.bgPanel, color: TEMA.ink,
          fontFamily: "'Inter', sans-serif", fontSize: '15px', fontWeight: 500,
        }}
      >
        <span style={{ flex: sidhuvud ? undefined : 1 }}>{aktivInfo.namn}</span>
        {!sidhuvud && iUtveckling(aktivInfo.id) && <UtvecklingsPiller />}
        {väntarAnnanstans && (
          <span aria-hidden="true" style={{ width: '7px', height: '7px', borderRadius: '50%', background: TEMA.accent, flexShrink: 0 }} />
        )}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" style={{
          transform: öppen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease', flexShrink: 0,
          color: TEMA.accent,
        }}>
          <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {öppen && (
        <div
          role="listbox"
          aria-label="Moduler"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', zIndex: 200,
            ...(sidhuvud ? { right: 0, width: '240px' } : { left: 0, right: 0 }),
            background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`, borderRadius: '6px', padding: '6px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
          }}
        >
          {moduler.map(m => {
            const ärAktiv = m.id === aktiv;
            const n = antal[m.id] ?? 0;
            return (
              <button
                key={m.id}
                role="option"
                aria-selected={ärAktiv}
                onClick={() => välj(m)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                  width: '100%', padding: '9px 10px', borderRadius: '4px', border: 'none', cursor: 'pointer',
                  textAlign: 'left', fontFamily: "'Inter', sans-serif",
                  background: ärAktiv ? 'rgba(224,168,62,0.10)' : 'transparent',
                }}
              >
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: ärAktiv ? TEMA.accent : TEMA.ink, fontWeight: 500 }}>
                    {m.namn}
                    {iUtveckling(m.id) && <UtvecklingsPiller />}
                  </span>
                  <span style={{ display: 'block', fontSize: '12px', color: TEMA.inkFaint, marginTop: '2px' }}>{m.beskrivning}</span>
                </span>
                {n > 0 && <span style={siffra(n, ärAktiv)} aria-label={`${n} att åtgärda`}>{n}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
